import { FC, SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';

import ClipboardListIcon from '@/assets/svg/ui-elements/clipboard-list.svg';
import UserCheckIcon from '@/assets/svg/ui-elements/user-check.svg';
import UserCircleIcon from '@/assets/svg/ui-elements/user-circle.svg';
import { MissingStep } from '@/features/profile-requirements/missing-steps';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { settingsTabKeys } from '@/pages/settings/settings-tab-keys';
import { PagePaths } from '@/routes/page-paths';
import { Modal, OptionCard, Typography } from '@/shared/design-system';

import { StepList } from './styled';

export type ProfileRequirementsIntent = 'create-entity' | 'switch-entity';

export type ProfileRequirementsModalProps = {
  intent: ProfileRequirementsIntent;
  missingSteps: MissingStep[];
  isBlocked: boolean;
};

const TITLES: Record<ProfileRequirementsIntent, string> = {
  'create-entity': 'Complete your profile to add an entity',
  'switch-entity': 'Complete your profile to open this entity',
};

const DESCRIPTIONS: Record<ProfileRequirementsIntent, string> = {
  'create-entity': 'Finish the steps below and entity creation opens up.',
  'switch-entity': 'Finish the step below and this entity opens up.',
};

const STEP_CONTENT: Record<
  MissingStep,
  { icon: FC<SVGProps<SVGSVGElement>>; title: string; subtitle: string }
> = {
  kyc: {
    icon: UserCheckIcon,
    title: 'Complete Your KYC',
    subtitle: 'Verify your identity with our provider',
  },
  questionnaire: {
    icon: ClipboardListIcon,
    title: 'Complete the Investor’s Questionnaire',
    subtitle: 'Confirm what you are eligible to invest in',
  },
  profile: {
    icon: UserCircleIcon,
    title: 'Fill Out Your Profile',
    subtitle: 'Add the details we are still missing',
  },
};

const PROFILE_MANDATORY_FIELDS_PATH = `${PagePaths.settings}?tab=${settingsTabKeys.myProfile}&profileTab=public&highlightMandatory=1`;

export const ProfileRequirementsModal = () => {
  const { open, handleClose, props } = useGlobalModal('PROFILE_REQUIREMENTS_MODAL');
  const { handleOpen: openKycModal } = useGlobalModal('KYC_START_MODAL');
  const navigate = useNavigate();

  const intent = props?.intent ?? 'create-entity';
  const isBlocked = !!props?.isBlocked;
  const missingSteps = isBlocked ? [] : (props?.missingSteps ?? []);

  const startStep = (step: MissingStep) => {
    handleClose();

    if (step === 'kyc') {
      openKycModal();
      return;
    }

    navigate(step === 'questionnaire' ? PagePaths.questionnaire : PROFILE_MANDATORY_FIELDS_PATH);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      variant="center"
      titleVariant="h6"
      title={isBlocked ? 'Your profile is blocked' : TITLES[intent]}
      description={isBlocked ? undefined : DESCRIPTIONS[intent]}
      maxWidth={560}
    >
      {isBlocked ? (
        <Typography variant="body2" align="center">
          Your profile has been blocked by a Gamma Prime administrator. Please reach out to the
          Gamma Prime team for clarifications.
        </Typography>
      ) : (
        <StepList>
          {missingSteps.map((step) => {
            const { icon: StepIcon, title, subtitle } = STEP_CONTENT[step];

            return (
              <li key={step}>
                <OptionCard
                  icon={<StepIcon />}
                  title={title}
                  subtitle={subtitle}
                  onClick={() => startStep(step)}
                />
              </li>
            );
          })}
        </StepList>
      )}
    </Modal>
  );
};
